/** Per-module access for the logged-in role, keyed by MenuMaster.menuName.
 *
 * The Roles screen only offers View / Edit / Delete, so those are the three
 * flags enforced here. "Add" is never set by that screen, so creating a record
 * counts as Edit. */
export interface ModuleAccess {
  view: boolean;
  edit: boolean;
  delete: boolean;
}

export type ModuleAction = keyof ModuleAccess;

/** Admin sections: only the account's Primary Admin (set by the Platform
 * Admin in Control Center) may add, edit or delete here. Everyone else is
 * view-only, whatever the Roles screen grants. The API enforces the same
 * rule (Filters/PrimaryAdminWriteAttribute). */
export const ADMIN_SECTION_MENUS = [
  "taxservicesettings",
  "roles",
  "users",
  "branches",
  "statusmasters",
];

/** menumaster rows were entered by hand and aren't consistently cased — the
 * live table has "roomservice" and "eventtypes" where the UI says roomService
 * and eventTypes — so every comparison goes through this. */
export const normalizeMenuName = (name: string) => name.trim().toLowerCase();

interface RouteModule {
  /** First path segment after /admin ("" is the dashboard). */
  segment: string;
  menuName: string;
  /** Sub-paths that create or change records, so opening them at all needs
   * Edit — e.g. bookings/new. Matched against the rest of the path. */
  editPaths?: RegExp;
  /** Where to send a user whose role can't see the dashboard. Only listing
   * pages qualify. */
  landing?: boolean;
}

/** Every /admin route and the menu it belongs to. Order is the sidebar's, so
 * the first viewable landing page is the one a user would see first. */
const ROUTE_MODULES: RouteModule[] = [
  { segment: "", menuName: "dashboard", landing: true },
  { segment: "bookings", menuName: "bookings", editPaths: /^\/(new|edit)(\/|$)/, landing: true },
  { segment: "checkout", menuName: "bookings", editPaths: /.*/ },
  // The tax-invoice register (room + event invoices and their GST e-invoices).
  // View-only to open; generating / cancelling an e-invoice from it is a POST,
  // so it still needs Bookings edit — and a billing role on the server.
  { segment: "tax-invoices", menuName: "bookings", landing: true },
  { segment: "customers", menuName: "customers", landing: true },
  { segment: "events", menuName: "events", editPaths: /^\/[^/]+\/edit(\/|$)/, landing: true },
  { segment: "create-event", menuName: "events", editPaths: /.*/ },
  { segment: "quotations", menuName: "quotations", editPaths: /^\/(new|[^/]+\/edit)(\/|$)/, landing: true },
  { segment: "room-service", menuName: "roomService", landing: true },
  { segment: "housekeeping", menuName: "housekeeping", landing: true },
  { segment: "restaurant", menuName: "restaurant", landing: true },
  { segment: "kot", menuName: "restaurant" },
  { segment: "restaurant-menu", menuName: "restaurant" },
  { segment: "food-hall", menuName: "foodHall", landing: true },
  { segment: "kitchen-types", menuName: "kitchenTypes", landing: true },
  { segment: "kitchens", menuName: "kitchens", landing: true },
  { segment: "kitchen-categories", menuName: "kitchenCategories", landing: true },
  { segment: "settings", menuName: "general", landing: true },
  { segment: "printers", menuName: "general" },
  { segment: "halls", menuName: "halls", landing: true },
  { segment: "rooms", menuName: "rooms", landing: true },
  { segment: "packages", menuName: "packages", landing: true },
  { segment: "event-types", menuName: "eventTypes", landing: true },
  { segment: "users", menuName: "users", landing: true },
  { segment: "roles", menuName: "roles", landing: true },
  { segment: "branches", menuName: "branches", landing: true },
  { segment: "amenities", menuName: "amenities", landing: true },
  { segment: "buildings", menuName: "buildings", landing: true },
  { segment: "floors", menuName: "floors", landing: true },
  { segment: "facilities", menuName: "facilities", landing: true },
  { segment: "status-masters", menuName: "statusMasters", landing: true },
  { segment: "food-categories", menuName: "foodCategories", landing: true },
  { segment: "food-menus", menuName: "foodMenus", landing: true },
  { segment: "food-packages", menuName: "foodPackages", landing: true },
  { segment: "additional-services", menuName: "additionalServices", landing: true },
  { segment: "guest-service-categories", menuName: "guestServiceCategories", landing: true },
  { segment: "guest-service-items", menuName: "guestServiceItems", landing: true },
  { segment: "rate-plans", menuName: "ratePlans", landing: true },
  { segment: "channel-manager", menuName: "channelManager", landing: true },
  { segment: "meal-types", menuName: "mealTypes", landing: true },
  { segment: "meal-plans", menuName: "mealPlans", landing: true },
  { segment: "tax-service-settings", menuName: "taxServiceSettings", landing: true },
];

export interface PathModule {
  menuName: string;
  /** Opening this path at all needs Edit, not just View. */
  needsEdit: boolean;
}

/** The module an /admin path belongs to, or null for paths outside /admin
 * (login, guest app, Control Center), which aren't role-gated. */
export const moduleForPath = (pathname: string): PathModule | null => {
  const match = /^\/admin(?:\/([^/]+))?(\/.*)?$/.exec(pathname.replace(/\/+$/, ""));
  if (!match) return null;
  const segment = match[1] ?? "";
  const rest = match[2] ?? "";
  const route = ROUTE_MODULES.find((r) => r.segment === segment);
  if (!route) return null;
  return {
    menuName: route.menuName,
    needsEdit: !!route.editPaths && route.editPaths.test(rest),
  };
};

/** The first listing page the given check allows, for a user whose role
 * can't see the dashboard. */
export const firstAllowedPath = (canView: (menuName: string) => boolean) => {
  const route = ROUTE_MODULES.find((r) => r.landing && canView(r.menuName));
  if (!route) return null;
  return route.segment ? `/admin/${route.segment}` : "/admin";
};

/** POSTs that only read — they compute or check something and save nothing,
 * so a View-only role may still make them. */
const READ_ONLY_POSTS = [
  /^\/auth\/login$/,
  /^\/eventbookings\/check-availability$/,
  /^\/eventquotations\/preview$/,
  // Test Connection: a read-only Fetch Inventory against Aiosell. Check
  // Against Aiosell is NOT here: it imports bookings and resends numbers.
  /^\/channel-manager\/configs\/\d+\/test-connection$/,
  // Signing a print request for QZ Tray changes nothing; any page may print.
  /^\/print\/sign$/,
];

/** Which permission an API call needs, or null when it only reads. */
export const actionForRequest = (
  method: string | undefined,
  path: string
): ModuleAction | null => {
  const verb = (method ?? "GET").toUpperCase();
  if (verb === "GET" || verb === "HEAD" || verb === "OPTIONS") return null;
  if (verb === "DELETE") return "delete";
  const bare = path.split("?")[0];
  if (verb === "POST" && READ_ONLY_POSTS.some((p) => p.test(bare))) return null;
  return "edit";
};

// ---- Request guard, installed by PermissionsProvider --------------------

/** Returns a message when the call must be refused, or null to let it go. */
type RequestGuard = (method: string | undefined, path: string) => string | null;

let requestGuard: RequestGuard | null = null;

export const setRequestGuard = (guard: RequestGuard | null) => {
  requestGuard = guard;
};

export const checkRequest = (method: string | undefined, path: string) =>
  requestGuard ? requestGuard(method, path) : null;

/** Fired after a role's permissions are saved, so the current session picks
 * the change up without a re-login. */
export const PERMISSIONS_CHANGED_EVENT = "ich:permissions-changed";

export const notifyPermissionsChanged = () =>
  window.dispatchEvent(new Event(PERMISSIONS_CHANGED_EVENT));
